import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  IconButton,
  Chip,
  Button,
} from "@mui/material";
import { Close, ContentCopy, Shortcut, Refresh } from "@mui/icons-material";
import usePhrasebookStore from "./phrasebook/store";
import { useEffect, useState, useRef } from "react";
import {
  determineTitle,
  generateDescription,
  getTitleTemplatePatterns,
} from "../../api/multiagents";
import { PhraseType } from "../../api/phrasebook";
import { BrandBatchSettings } from "../../pages/playground/types";
import { getGenerationLabel, getGeneratingMessage } from "../../utils/fieldNames";

export default function EditorGeneration({
  appendPhrase,
  replaceAllPhrases,
  titleTemplates,
  batchSettings,
}: {
  appendPhrase: (type: PhraseType, phrase: string) => void;
  replaceAllPhrases: (type: PhraseType, phrases: string[]) => void;
  titleTemplates?: string[];
  batchSettings?: BrandBatchSettings;
}) {
  const {
    isGenerating,
    jobId,
    setIsGenerating,
    generatedContent,
    setGeneratedContent,
  } = usePhrasebookStore();
  const [loading, setLoading] = useState(false);
  const [variants, setVariants] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [forceRegenerate, setForceRegenerate] = useState(false);

  // Use a ref to access generatedContent without triggering effect re-runs
  const generatedContentRef = useRef(generatedContent);

  // Keep the ref in sync with the store value
  useEffect(() => {
    generatedContentRef.current = generatedContent;
  }, [generatedContent]);

  const type = isGenerating && isGenerating?.type;
  const ts = isGenerating && isGenerating?.ts;

  type TemplatePattern = {
    name: string;
    value: string;
  };

  const [availableTemplatePatterns, setAvailableTemplatePatterns] = useState<
    TemplatePattern[]
  >([]);

  useEffect(() => {
    getTitleTemplatePatterns().then((res) => {
      setAvailableTemplatePatterns(res.data.data.patterns);
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if we have cached content and not forcing regeneration
        // Use the ref to avoid re-triggering this effect when cache is updated
        if (!forceRegenerate && type && generatedContentRef.current[type]) {
          setVariants(generatedContentRef.current[type]);
          setLoading(false);
          return;
        }

        let res;

        if (type === "title") {
          res = await determineTitle({ job_id: jobId });
        } else if (type && type.endsWith("Description")) {
          res = await generateDescription({
            job_id: jobId,
            description_type: type,
          });
        }

        if (res?.status === 200) {
          if (res?.data?.data?.variants) {
            const newVariants = res.data.data.variants;
            setVariants(newVariants);
            // Cache the generated content
            if (type) {
              setGeneratedContent(type, newVariants);
            }
          } else {
            setVariants([]);
          }
        }
      } catch (e: unknown) {
        setVariants([]);

        // Use the specific error message from the backend
        const errorMessage =
          (e as { response: { data: { message: string } } })?.response?.data
            ?.message ||
          (e as { message: string })?.message ||
          "An unexpected error occurred.";
        setError(errorMessage);
      } finally {
        setLoading(false);
        setForceRegenerate(false);
      }
    };

    if (type && ts) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, ts, jobId, forceRegenerate]);

  const handleClose = () => {
    setIsGenerating(false);
    setVariants([]);
  };

  const addDotIfNeeded = (phrase: string): string => {
    // Only add dots for descriptions and only if doesn't end with punctuation
    if (
      type &&
      type.endsWith("Description") &&
      phrase.length > 0 &&
      !/[.!?;:,…–—]$/.test(phrase)
    ) {
      return phrase + ".";
    }
    return phrase;
  };

  let phraseType: PhraseType;

  switch (type) {
    case "title":
      phraseType = PhraseType.TITLE;
      break;
    case "regularDescription":
      phraseType = PhraseType.EDITORIAL_DESCRIPTION;
      break;
    case "shortDescription":
      phraseType = PhraseType.SHORT_DESCRIPTION;
      break;
    case "longDescription":
      phraseType = PhraseType.LONG_DESCRIPTION;
      break;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 2,
        height: "100%",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          {getGenerationLabel(type, batchSettings)}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            variant="outlined"
            size="small"
            disabled={loading}
            onClick={() => {
              setForceRegenerate(true);
              if (type) {
                setIsGenerating({ type, ts: Date.now() });
              }
            }}
            startIcon={<Refresh />}
          >
            Regenerate
          </Button>
          <IconButton size="small" onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            p: 4,
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            {getGeneratingMessage(type, batchSettings)}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ overflow: "auto" }}>
          {type === "title" && titleTemplates && titleTemplates.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
              >
                <strong>Current template patterns set:</strong>
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}
                >
                  {titleTemplates.map((templateName) => {
                    // Find the matching pattern for this template name
                    const pattern = availableTemplatePatterns.find(
                      (p) => p.name === templateName
                    );
                    return pattern ? (
                      <Chip
                        key={templateName}
                        size="small"
                        label={pattern.value}
                        variant="outlined"
                        sx={{ fontSize: "0.75rem" }}
                      />
                    ) : null;
                  })}
                </Box>
              </Typography>
            </Box>
          )}

          {variants.length > 0 ? (
            <>
              <Box sx={{ mb: 2 }}>
                {variants.map((variant, variantIndex) => (
                  <Paper
                    key={variantIndex}
                    variant="outlined"
                    sx={{ p: 1, backgroundColor: "grey.50", mb: 1 }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <IconButton
                        size="small"
                        title="Copy entire variant"
                        onClick={() => {
                          const variantPhrases = variant
                            .split("|")
                            .map((phrase) => phrase.trim())
                            .filter((phrase) => phrase.length > 0)
                            .map(addDotIfNeeded);
                          replaceAllPhrases(phraseType, variantPhrases);
                        }}
                      >
                        <Shortcut
                          fontSize="small"
                          sx={{
                            color: "text.primary",
                          }}
                        />
                      </IconButton>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: "bold", ml: 0.5 }}
                      >
                        Variant {variantIndex + 1}
                      </Typography>
                    </Box>
                    {variant
                      .split("|")
                      .map((phrase) => phrase.trim())
                      .filter((phrase) => phrase.length > 0)
                      .map(addDotIfNeeded)
                      .map((phrase, phraseIndex) => (
                        <Box
                          key={phraseIndex}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 0.5,
                            px: 0.5,
                            borderRadius: 1,
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => appendPhrase(phraseType, phrase)}
                          >
                            <ContentCopy
                              sx={{
                                fontSize: "small",
                                color: "text.primary",
                              }}
                            />
                          </IconButton>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              flexGrow: 1,
                            }}
                          >
                            {phrase}
                          </Typography>
                        </Box>
                      ))}
                  </Paper>
                ))}
              </Box>
            </>
          ) : (
            <Typography
              variant="body2"
              color={error ? "error.main" : "text.secondary"}
            >
              {error || "No phrases generated. Try regenerating."}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
